/**  Common for image data construction for the altAssets and assetAuditors */
import db from "../../common/lib/database";
import { get, set, update } from "idb-keyval";

/** Image construction for auditAsset and AltAsset */
const mapAuditImages = (auditImages: Array) =>
    auditImages.map(
        ({ createdDate, imageType, latitude, longitude, image, uploaded }) => ({
            createdDate,
            imageType,
            latitude,
            longitude,
            path: image,
            uploaded,
        })
    );

/**  Object construction for the altAssets */
const mapAltAsset = async (altAsset: any) => {
    const auditImages = altAsset.auditImages
        ? mapAuditImages(altAsset.auditImages)
        : [];
    return {
        altAsset: {
            createdBy: altAsset.createdBy,
            createdDate: altAsset.createdDate,
            identifier1: altAsset.identifier1,
            identifier2: altAsset.identifier2,
            identifier3: altAsset.identifier3,
            identifier4: altAsset.identifier4,
            make: altAsset.make,
            type: altAsset.type,
        },
        auditImages,
        notes: altAsset.notes ? altAsset.notes : [],
    };
};

/** Object construction for the  assetAuditor  */
const mapAssetAuditor = async (assetAuditor: any) => {
    const auditImages = assetAuditor.auditImages
        ? mapAuditImages(assetAuditor.auditImages)
        : [];

    return {
        assetAuditor: {
            assignedDate: assetAuditor.assignedDate,
            auditAsset: assetAuditor.auditAsset.id,
            auditor: assetAuditor.auditAssignment.auditor,
            exclusionReason: assetAuditor.exclusionReason,
            exclusionReasonDate: assetAuditor.exclusionReasonDate,
            status: assetAuditor.status,
        },
        assetAttributes: assetAuditor.auditAsset.assetAttributes,
        auditImages,
        auditLocation: assetAuditor.auditAssignment.auditLocation.id,
        notes: assetAuditor.notes ? assetAuditor.notes : [],
    };
};

/** Clearing alt assets  */
const clearAltAssset = async (audit) => {
    const altAssetsDB = (await get(db.types.altAsset)) || [];
    const altAssets = db.queries.forAudit(audit.id, altAssetsDB);
    const altAssetsIds = altAssets.map((item) => item.id);

    // Update Alt Assets by filtering out alt  assets for submitted audit and updating db
    await update(db.types.altAsset, (updatedAltAssetsDB = []) => {
        if (!updatedAltAssetsDB) updatedAltAssetsDB = [];
        return updatedAltAssetsDB.filter(
            (item) => !altAssetsIds.includes(item.id)
        );
    });
};

/** Clearing assetAuditor  */

const clearAssetAuditor = async (audit) => {
    //Clear Asset Auditor this can be retrived through auditAssignment DB and map the ids with assetAuditors
    const auditAssignmentDB = (await get(db.types.auditAssignment)) || [];
    const auditAssignments = db.queries.forAuditLocationAudit(
        audit.id,
        auditAssignmentDB
    );

    // TODO:(clearing unused records) const assignmentIdsToRemove = [];
    const assignmentIds = [];
    auditAssignments.map((item) => {
        assignmentIds.push(item.id);
    });

    const assetAuditorsDB = (await get(db.types.assetAuditor)) || [];
    const assetAuditors = db.queries.forAuditAssignments(
        assignmentIds,
        assetAuditorsDB
    );

    const assetAuditorsId = assetAuditors.map((item) => `${item.id}`);

    // Update assetAuditors by filtering out assets for submitted audit and updating db

    await update(db.types.assetAuditor, (updatedAssetAuditorsDB = []) => {
        if (!updatedAssetAuditorsDB) updatedAssetAuditorsDB = [];
        return updatedAssetAuditorsDB.filter(
            (item) => !assetAuditorsId.includes(item.id)
        );
    });
};

const cleanAllGroupData = async (audit) => {
    // Leave only submissions that are linked to other audits
    clearAltAssset(audit);
    clearAssetAuditor(audit);
    cleanAllData(audit);
    // Remove audit group from Audit Assignment DB
    await update(db.types.auditAssignment, (updatedAuditAssignmentDB = []) => {
        if (!updatedAuditAssignmentDB) updatedAuditAssignmentDB = [];
        return updatedAuditAssignmentDB.filter(
            (item) =>
                item.auditLocation.audit.auditReference !== audit.auditReference
        );
    });

    // Remove AuditAsset data
    await update(db.types.auditAsset, (updatedAuditAssetDB = []) => {
        if (!updatedAuditAssetDB) updatedAuditAssetDB = [];
        return updatedAuditAssetDB.filter(
            (item) =>
                item.auditLocation.audit.auditReference !== audit.auditReference
        );
    });
};
/** Clearing data of Altassets, AsssetAuditor, AuditAssets and AuditAssignments */
const cleanAllData = async (audit) => {
    clearAltAssset(audit);
    clearAssetAuditor(audit);

    // Leave only submissions that are linked to other audits
    await update(db.types.pendingSubmission, (updatedSubmissionDB = []) => {
        if (!updatedSubmissionDB) updatedSubmissionDB = [];
        return updatedSubmissionDB.filter((item) => item.auditId !== audit.id);
    });

    const pendingSubmissionDB = (await get(db.types.pendingSubmission)) || [];
    // Clear data when there are no audits to display on user's device
    if (pendingSubmissionDB.length === 0) {
        /* 
            We cannot delete records below, because some of them are used among the audits
            and contain linked adata
         */
        //Empty if there are  no more audit assignments
        await set(db.types.auditAssignment, []);
        await set(db.types.auditAsset, []);
        await set(db.types.typesList, []);
    }
};

const delayFn = (baseMs = 3000, attempt = 1, maxMs = 30000) => {
    const expDelay = Math.min(baseMs * Math.pow(2, attempt - 1), maxMs);
    const jitter = Math.random() * 300;
    const finalDelay = expDelay + jitter;

    return new Promise((resolve) => setTimeout(resolve, finalDelay));
};

/** Clearing data after partial submission */
const cleanPartialData = (audit) => {
    clearAltAssset(audit);
    clearAssetAuditor(audit);
};

export default {
    cleanAllData,
    cleanPartialData,
    cleanAllGroupData,
    mapAssetAuditor,
    mapAltAsset,
    mapAuditImages,
    delayFn,
};
/***TODO
 * 1. cleanAllGroupData test case
 */
